import { useEffect, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Coffee, QrCode, Stamp, Timer, Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReviewQR } from "@/components/ReviewQR";
import { OpeningBadge, usePublicInfo } from "@/components/OpeningBadge";

const HERO = "https://images.unsplash.com/photo-1788404805000-658983b59cde?auto=format&fit=crop&w=1600&q=80";
const CUSTOM_HERO = `${process.env.REACT_APP_BACKEND_URL}/api/hero`;

export default function LoginPage() {
  const { t, lang, setLang } = useLanguage();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [heroSrc, setHeroSrc] = useState(CUSTOM_HERO);
  const publicInfo = usePublicInfo();

  useEffect(() => {
    if (loading || !user) return;
    const pendingTable = sessionStorage.getItem("pending_table");
    sessionStorage.removeItem("pending_table");
    const base = user.role === "staff" && !pendingTable ? "/staff" : "/menu";
    navigate(pendingTable ? `${base}?table=${pendingTable}` : base, { replace: true });
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem("oauth_state", state);
    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: window.location.origin + "/auth/google",
      response_type: "code",
      scope: "openid email profile",
      state,
      prompt: "select_account",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const features = [
    { icon: QrCode, text: t("feat_menu") },
    { icon: Stamp, text: t("feat_stamps") },
    { icon: Timer, text: t("feat_orders") },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="login-page">
      <div className="relative h-[52vh] min-h-[340px] overflow-hidden">
        <img src={heroSrc} onError={() => setHeroSrc(HERO)} alt="La Caffetteria del Fortino" data-testid="login-hero-img" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#120D0C]/90 via-[#120D0C]/40 to-transparent" />
        <Button
          data-testid="lang-switcher"
          variant="ghost"
          size="sm"
          className="absolute top-4 right-4 z-10 gap-1 text-[#F5EFE6] hover:bg-white/10 hover:text-white"
          onClick={() => setLang(lang === "it" ? "en" : "it")}
        >
          <Languages className="w-4 h-4" />
          <span className="uppercase text-xs font-bold">{lang === "it" ? "EN" : "IT"}</span>
        </Button>
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 max-w-3xl">
          <div className="flex items-center gap-2 text-[#F4A261] text-xs uppercase tracking-widest font-semibold mb-3">
            <Coffee className="w-4 h-4" /> {t("tagline")}
          </div>
          <p className="text-[#F4A261] font-semibold tracking-wide mb-1" data-testid="login-brand">{t("brand")}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#F5EFE6] leading-tight">
            {t("login_title")}
          </h1>
          <p className="mt-3 text-[#F5EFE6]/80 text-base sm:text-lg max-w-md">{t("login_subtitle")}</p>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 sm:px-10 -mt-2 py-10">
        <Button
          data-testid="google-login-btn"
          onClick={handleGoogleLogin}
          className="w-full sm:w-auto bg-[#4A2C2A] hover:bg-[#36201F] text-[#FDFBF7] rounded-full px-8 h-12 text-base font-semibold shadow-lg transition-transform hover:scale-[1.02]"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" opacity=".7" />
            <path fill="currentColor" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" opacity=".5" />
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" opacity=".9" />
          </svg>
          {t("login_google")}
        </Button>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {features.map((f, i) => (
            <div key={i} className="bg-white border border-[#E8DFD5] rounded-2xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
              <f.icon className="w-6 h-6 text-[#D97706] mb-3" />
              <p className="text-sm font-medium text-[#1F1615]">{f.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-6">
          <OpeningBadge info={publicInfo} />
        </div>
        <div className="mt-6">
          <ReviewQR />
        </div>
      </div>
    </div>
  );
}
